import { getFormErrorMessage } from "@core/utility/get-error-message";
import { UtilAgeBracketFormRule, UtilAgeBracketFormSchema } from "@factory/form/form.rule";
import { useFormDefaultUtilAgeBracket } from "@factory/form/form-default-util-age-bracket.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseUtilAgeBracketModel } from "@factory/model/query-model-util-age-bracket";
import { 
  useQyDeleteUtilAgeBracket, 
  useQyGetUtilAgeBracketById, 
  useQyUpdateUtilAgeBracket} from "@factory/query/util-age-bracket.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditUtilAgeBracket() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultUtilAgeBracket();
  const navigate = useNavigate();
  const { mapUpdateUtilAgeBracket, mapDeleteUtilAgeBracket } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { utilAgeBracketId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID UtilAgeBracket
  const handleGetApiSuccess = (data: GetResponseUtilAgeBracketModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("type", responseData?.type || "");
      setValue("name", responseData?.name || "");
      setValue("price", responseData?.price);
  };
  const {
    data: utilAgeBracketResponse,
    isLoading,
    isError: utilAgeBracketError,
  } = useQyGetUtilAgeBracketById(utilAgeBracketId || "", handleGetApiSuccess);
  const utilAgeBracketData = utilAgeBracketResponse?.data?.[0];

  // API UPDATE UtilAgeBracket
  const handleUpdateApiSuccess = () => {
    showSuccess("UtilAgeBracket updated successfully");
  };
  const { mutate: updateUtilAgeBracket, isLoading: isEditLoading } = useQyUpdateUtilAgeBracket(
    handleUpdateApiSuccess
  );

  // API DELETE UtilAgeBracket
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("UtilAgeBracket deleted successfully");
  };
  const { mutate: deleteUtilAgeBracket } = useQyDeleteUtilAgeBracket(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilAgeBracketFormSchema>({
    defaultValues: getDefault(utilAgeBracketData),
    resolver: zodResolver(UtilAgeBracketFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: UtilAgeBracketFormSchema) => {
    if (!utilAgeBracketData || !utilAgeBracketData.code) {
      throw new Error("No utilAgeBracket data");
    }

    const formData = mapUpdateUtilAgeBracket(form, utilAgeBracketData.code);
    updateUtilAgeBracket(formData);
  };
  const handleValidateError = (err: FieldErrors<UtilAgeBracketFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!utilAgeBracketData || !utilAgeBracketData.code) {
      throw new Error("No utilAgeBracket data found");
    }

    const form = mapDeleteUtilAgeBracket(utilAgeBracketData.code);
    deleteUtilAgeBracket(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    utilAgeBracketId,
    utilAgeBracketData,
    isLoading,
    utilAgeBracketError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}